/**
 * DAVIDsTEA — lets ordinary in-page links open an investor relations tab.
 *
 * Investor relations used to be five separate pages; this theme folds them into one tabbed
 * template (sections/dt-investor-relations.liquid). Copy carried over from those pages still
 * cross-references them — the FAQ's "you can view our Board of Directors here" pointed at
 * /pages/management-board-of-directors, which is now the Corporate Governance tab of the very
 * page the reader is already on. Rather than rewrite that sentence into "see the tab above",
 * the link keeps working: it targets the tab by id and this opens it in place.
 *
 * Nothing here reaches into <dt-tabbed-info>. That component (assets/dt-tabbed-info.js) is
 * shared with the product page and already listens for clicks on its own [role="tab"]
 * descendants, so forwarding a real click to the button is enough to move the selection, and
 * this file stays a sidecar the shared component knows nothing about.
 *
 * Focus moves to the tab afterwards, not just the scroll position. A sighted reader follows
 * the jump by eye; without the focus move a keyboard or screen reader user activates the link
 * and stays where they were, with the page silently rearranged somewhere else.
 *
 * Links are written as `#tab-<block id>` — blocks/dt-ir-tab.liquid gives each button exactly
 * that id, so the href is checkable against the rendered page and breaks loudly (an ordinary
 * dead anchor) rather than silently if a tab is ever removed.
 */

/** @param {Element | null} tab */
function activate(tab) {
  if (!(tab instanceof HTMLElement) || tab.getAttribute('role') !== 'tab') return false;

  // A real click, so <dt-tabbed-info>'s own delegated handler does the selecting — the
  // aria-selected/tabindex/hidden bookkeeping lives there and is not duplicated here.
  tab.click();

  // The component only moves focus when the arrow keys drove the change, which is correct for
  // its own keyboard pattern but leaves a link activation soundless. Do it here instead.
  tab.focus();
  tab.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  return true;
}

/**
 * Exact id first, then the same id as a suffix.
 *
 * blocks/dt-ir-tab.liquid writes id="tab-{{ block.id }}", and for a block declared in a JSON
 * template `block.id` is that block's own key — `tab-tab_governance` here. Shopify does prefix
 * a parent's id onto a nested theme block's, though, so a tab that ever stops being a direct
 * child of the section would render a longer id than the href names. The suffix pass keeps the
 * link working through that move instead of turning into a dead anchor nobody notices.
 *
 * @param {string} hash
 */
function tabFor(hash) {
  if (!hash || hash.length < 2) return null;
  try {
    const exact = document.querySelector(`${hash}[role="tab"]`);
    if (exact) return exact;
  } catch {
    // A hash that isn't a valid selector is somebody else's anchor, not ours.
    return null;
  }

  const key = hash.slice(1);
  return (
    [...document.querySelectorAll('[role="tab"][id]')].find(
      (tab) => tab.id === key || tab.id.endsWith(`_${key.replace(/^tab-/, '')}`)
    ) ?? null
  );
}

document.addEventListener('click', (event) => {
  const link = /** @type {HTMLElement} */ (event.target)?.closest?.('a[href*="#tab-"]');
  if (!(link instanceof HTMLAnchorElement)) return;

  // Only same-document links: an absolute href to another page that happens to carry the
  // fragment should still navigate.
  if (link.pathname !== window.location.pathname) return;

  if (activate(tabFor(link.hash))) {
    event.preventDefault();
    // Keep the address bar honest so the tab survives a reload or a shared link.
    window.history.replaceState(null, '', link.hash);
  }
});

// Arriving with a fragment already set — a shared link, or a reload after the click above.
// Waits for the element to upgrade, since the selection is the component's to make and its
// click listener does not exist until then.
customElements.whenDefined('dt-tabbed-info').then(() => {
  activate(tabFor(window.location.hash));
});
